import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Row,
  Col,
  Card,
  DatePicker,
  Select,
  Table,
  Tag,
  Space,
  Typography,
  Statistic,
  Progress,
  Button,
  Tooltip,
  Input,
} from 'antd';
import {
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
  DownloadOutlined,
  ReloadOutlined,
  SafetyOutlined,
  LinkOutlined,
  GlobalOutlined,
  MobileOutlined,
  DesktopOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import * as echarts from 'echarts';
import dayjs from 'dayjs';
import { statisticsService } from '../services/statistics';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import InterceptReasonsTable from '../components/InterceptReasonsTable';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title, Text } = Typography;

interface StatisticsData {
  total_visits: number;
  default_redirects: number;
  intercept_redirects: number;
  success_rate: number;
  realtime_visits: number;
  top_links: Array<{
    id: number;
    title: string;
    total_visits: number;
    default_redirects: number;
    intercept_redirects: number;
    success_rate: number;
  }>;

  geography_distribution: Array<{
    country: string;
    visits: number;
    percentage: number;
  }>;
  device_distribution: Array<{
    device_type: string;
    visits: number;
    percentage: number;
  }>;
  network_distribution: Array<{
    network_type: string;
    visits: number;
    percentage: number;
  }>;

}

const Statistics: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // 从 URL 参数或 state 中获取初始值
  const urlShortCode = searchParams.get('short_code') || '';
  const fromLinkManagement = location.state?.fromLinkManagement;
  const linkId = location.state?.linkId;
  const linkTitle = location.state?.linkTitle;
  
  const [statisticsData, setStatisticsData] = useState<StatisticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>(() => [
    dayjs(), 
    dayjs()
  ]);
  const [selectedLink, setSelectedLink] = useState<string>(fromLinkManagement && linkId ? linkId.toString() : 'all');
  // 输入状态（用户输入时更新）
  const [clientIdInput, setClientIdInput] = useState<string>('');
  const [shortCodeInput, setShortCodeInput] = useState<string>(urlShortCode);
  // 确认搜索状态（点击搜索按钮时更新）
  const [appliedClientId, setAppliedClientId] = useState<string>('');
  const [appliedShortCode, setAppliedShortCode] = useState<string>(urlShortCode);
  
  // 用于取消请求的 AbortController
  const abortControllerRef = useRef<AbortController | null>(null);
  // 用于防抖的定时器
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const loadData = useCallback(async () => {
    console.log('🔄 loadData called at:', new Date().toISOString());
    
    // 清除之前的防抖定时器
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // 取消之前的请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // 创建新的 AbortController
    abortControllerRef.current = new AbortController();
    
    // 设置防抖延迟
    debounceTimerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        console.log('📊 开始加载统计数据...');
        
        // 确定要使用的链接ID
        let targetLinkId: number | undefined;
        if (fromLinkManagement && linkId) {
          targetLinkId = linkId;
        } else if (selectedLink && selectedLink !== 'all') {
          targetLinkId = parseInt(selectedLink);
        }
        
        console.log('🔗 目标链接ID:', targetLinkId);
        console.log('📋 来自链接管理:', fromLinkManagement);
        
        // 解析客户ID（使用确认后的值）
        const parsedClientId = appliedClientId ? parseInt(appliedClientId) : undefined;
        const validClientId = parsedClientId && !isNaN(parsedClientId) ? parsedClientId : undefined;
        
        console.log('👤 客户ID:', validClientId);
        console.log('🔗 短链码:', appliedShortCode || '无');
        
        // 使用新的完整统计数据获取方法，传递所有筛选参数
        const data = await statisticsService.getCompleteStats(
          targetLinkId,
          dateRange[0].format('YYYY-MM-DD'),
          dateRange[1].format('YYYY-MM-DD'),
          validClientId,
          appliedShortCode || undefined
        );
        console.log('✅ 统计数据加载成功:', data);
        
        // 检查请求是否被取消
        if (!abortControllerRef.current?.signal.aborted) {
          setStatisticsData(data);
          setLoading(false);
          console.log('📈 统计数据已设置到状态:', data);
        }
      } catch (error: any) {
        // 忽略被取消的请求错误
        if (error.name !== 'AbortError') {
          console.error('❌ 加载数据失败:', error);
          setLoading(false);
        }
      }
    }, 100); // 100ms 防抖延迟
  }, [selectedLink, fromLinkManagement, linkId, dateRange, appliedClientId, appliedShortCode]);

  // 监听搜索条件变化，直接触发数据加载
  useEffect(() => {
    console.log('🎯 搜索条件变化，触发数据加载');
    console.log('📊 appliedShortCode:', appliedShortCode);
    console.log('📊 appliedClientId:', appliedClientId);
    console.log('📅 dateRange:', dateRange);
    loadData();
    
    // 清理函数
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [selectedLink, dateRange, appliedClientId, appliedShortCode, loadData]);

  // 图表渲染 useEffect
  useEffect(() => {
    if (!statisticsData) return;



    // 初始化跳转比例图表
    const ratioChartElement = document.getElementById('redirectRatioChart');
    if (!ratioChartElement) {
      console.error('跳转比例图表容器不存在');
      return;
    }
    const ratioChart = echarts.init(ratioChartElement);
    const ratioOption = {
      title: {
        text: '跳转比例统计',
        left: 'center',
        textStyle: {
          fontSize: 16,
          fontWeight: 'bold',
        },
      },
      tooltip: {
        trigger: 'item',
        formatter: '{a} <br/>{b}: {c} ({d}%)',
      },
      legend: {
        orient: 'vertical',
        left: 'left',
        data: ['默认跳转', '拦截跳转'],
      },
      series: [
        {
          name: '跳转类型',
          type: 'pie',
          radius: '50%',
          data: [
            {
              value: statisticsData.default_redirects,
              name: '默认跳转',
              itemStyle: { color: '#52c41a' },
            },
            {
              value: statisticsData.intercept_redirects,
              name: '拦截跳转',
              itemStyle: { color: '#ff4d4f' },
            },
          ],
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        },
      ],
    };
    ratioChart.setOption(ratioOption);

    // 初始化地理位置分布图表
    const geographyChartElement = document.getElementById('geographyChart');
    if (!geographyChartElement) {
      console.error('地理位置图表容器不存在');
      return;
    }
    const geographyChart = echarts.init(geographyChartElement);
    const geographyOption = {
      title: {
        text: '访客地理位置分布',
        left: 'center',
        textStyle: {
          fontSize: 16,
          fontWeight: 'bold',
        },
      },
      tooltip: {
        trigger: 'item',
        formatter: '{a} <br/>{b}: {c} ({d}%)',
      },
      legend: {
        orient: 'vertical',
        left: 'left',
        type: 'scroll',
      },
      series: [
        {
          name: '地理位置',
          type: 'pie',
          radius: '50%',
          data: (statisticsData.geography_distribution || []).map(item => ({
            value: item.visits,
            name: item.country,
          })),
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        },
      ],
    };
    geographyChart.setOption(geographyOption);

    // 初始化设备类型分布图表
    const deviceChartElement = document.getElementById('deviceTypeChart');
    if (!deviceChartElement) {
      console.error('设备类型图表容器不存在');
      return;
    }
    const deviceChart = echarts.init(deviceChartElement);
    const deviceOption = {
      title: {
        text: '设备类型分布',
        left: 'center',
        textStyle: {
          fontSize: 16,
          fontWeight: 'bold',
        },
      },
      tooltip: {
        trigger: 'item',
        formatter: '{a} <br/>{b}: {c} ({d}%)',
      },
      legend: {
        orient: 'vertical',
        left: 'left',
        type: 'scroll',
      },
      series: [
        {
          name: '设备类型',
          type: 'pie',
          radius: '50%',
          data: (statisticsData.device_distribution || []).map(item => ({
            value: item.visits,
            name: item.device_type,
          })),
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        },
      ],
    };
    deviceChart.setOption(deviceOption);



    // 清理函数
    return () => {
      ratioChart.dispose();
      geographyChart.dispose();
      deviceChart.dispose();
    };
  }, [statisticsData]);

  const handleExport = () => {
    // 导出统计数据
    // message.info('导出功能开发中'); // message is not imported, so this line is commented out
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <div>正在加载统计数据...</div>
      </div>
    );
  }

  if (!statisticsData) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <div>暂无统计数据</div>
      </div>
    );
  }

  // 计算当前显示的统计数据
  const getCurrentStats = () => {
    if (selectedLink && selectedLink !== 'all') {
      // 如果选择了特定链接，返回该链接的统计数据
      const selectedLinkData = statisticsData.top_links?.find(link => link.id.toString() === selectedLink);
      if (selectedLinkData) {
        return {
          total_visits: selectedLinkData.total_visits,
          default_redirects: selectedLinkData.default_redirects,
          intercept_redirects: selectedLinkData.intercept_redirects,
          success_rate: selectedLinkData.success_rate,
        };
      }
    }
    // 否则返回总体统计数据
    return {
      total_visits: statisticsData.total_visits,
      default_redirects: statisticsData.default_redirects,
      intercept_redirects: statisticsData.intercept_redirects,
      success_rate: statisticsData.success_rate,
    };
  };

  const currentStats = getCurrentStats();

  return (
    <div style={{ padding: '24px' }}>
      {/* 页面标题 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {fromLinkManagement && (
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={() => navigate('/links')}
              style={{ marginRight: 16 }}
            >
              返回链接管理
            </Button>
          )}
          <div>
            <Title level={3} style={{ margin: 0 }}>
              <BarChartOutlined style={{ marginRight: 8 }} />
              {fromLinkManagement && linkTitle 
                ? `${linkTitle} - 统计分析` 
                : selectedLink && selectedLink !== 'all'
                  ? `${statisticsData.top_links?.find(link => link.id.toString() === selectedLink)?.title || '未知链接'} - 统计分析`
                  : '数据统计分析'
              }
            </Title>
            {fromLinkManagement && linkTitle && (
              <Text type="secondary" style={{ fontSize: '14px' }}>
                链接ID: {linkId}
              </Text>
            )}
          </div>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadData}>
            刷新数据
          </Button>
          <Button icon={<DownloadOutlined />} onClick={handleExport}>
            导出统计
          </Button>
        </Space>
      </div>

      {/* 统计概览卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="总访问量"
              value={currentStats.total_visits}
              prefix={<BarChartOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="默认跳转"
              value={currentStats.default_redirects}
              prefix={<LinkOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
            <Progress
              percent={currentStats.total_visits > 0 ? Math.round((currentStats.default_redirects / currentStats.total_visits) * 100) : 0}
              size="small"
              status="success"
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="拦截跳转"
              value={currentStats.intercept_redirects}
              prefix={<SafetyOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
            <Progress
              percent={currentStats.total_visits > 0 ? Math.round((currentStats.intercept_redirects / currentStats.total_visits) * 100) : 0}
              size="small"
              status="exception"
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="跳转成功率"
              value={Number((currentStats.success_rate ?? 0).toFixed(2))}
              suffix="%"
              prefix={<BarChartOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
            <Progress
              percent={currentStats.success_rate}
              size="small"
              status={currentStats.success_rate > 80 ? 'success' : currentStats.success_rate > 60 ? 'normal' : 'exception'}
            />
          </Card>
        </Col>
      </Row>

      {/* 筛选和操作区域 */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col span={8}>
            <Text strong>时间范围：</Text>
            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  console.log('📅 时间范围变化:', dates[0].format('YYYY-MM-DD'), '到', dates[1].format('YYYY-MM-DD'));
                  setDateRange([dates[0], dates[1]]);
                }
              }}
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={3}>
            <Text strong>客户ID：</Text>
            <Input
              value={clientIdInput}
              onChange={(e) => setClientIdInput(e.target.value)}
              onPressEnter={() => {
                setAppliedClientId(clientIdInput);
                setAppliedShortCode(shortCodeInput);
              }}
              placeholder="客户ID"
              allowClear
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={3}>
            <Text strong>短链码：</Text>
            <Input
              value={shortCodeInput}
              onChange={(e) => setShortCodeInput(e.target.value)}
              onPressEnter={() => {
                setAppliedClientId(clientIdInput);
                setAppliedShortCode(shortCodeInput);
              }}
              placeholder="短链码"
              allowClear
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={2}>
            <Text strong>&nbsp;</Text>
            <Button
              type="primary"
              onClick={() => {
                console.log('🔍 搜索按钮被点击');
                console.log('📝 客户ID输入:', clientIdInput);
                console.log('📝 短链码输入:', shortCodeInput);
                setAppliedClientId(clientIdInput);
                setAppliedShortCode(shortCodeInput);
                console.log('✅ 已更新搜索状态');
                // 直接触发数据加载
                setTimeout(() => {
                  console.log('⏰ 延迟后触发 loadData');
                  loadData();
                }, 50);
              }}
              style={{ width: '100%', marginTop: 0 }}
            >
              搜索
            </Button>
          </Col>
          <Col span={8} style={{ textAlign: 'right' }}>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={loadData}>
                刷新数据
              </Button>
              <Button icon={<DownloadOutlined />} onClick={handleExport}>
                导出统计
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 图表区域 */}
      <Row gutter={[16, 16]}>

        <Col span={12}>
          <Card title="跳转比例统计" extra={<PieChartOutlined />}>
            <div id="redirectRatioChart" style={{ height: '300px' }} />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="访客地理位置分布" extra={<GlobalOutlined />}>
            <div id="geographyChart" style={{ height: '300px' }} />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="设备类型分布" extra={<MobileOutlined />}>
            <div id="deviceTypeChart" style={{ height: '300px' }} />
          </Card>
        </Col>

      </Row>

      {/* 链接统计表格 */}
      <Card 
        title={
          selectedLink && selectedLink !== 'all' 
            ? `链接访问统计 - ${statisticsData.top_links?.find(link => link.id.toString() === selectedLink)?.title || '未知链接'}`
            : "链接访问统计"
        } 
        style={{ marginTop: 24 }}
      >
        <Table
          dataSource={
            selectedLink && selectedLink !== 'all' 
              ? (statisticsData.top_links || []).filter(link => link.id.toString() === selectedLink)
              : (statisticsData.top_links || [])
          }
          columns={[
            {
              title: '链接名称',
              dataIndex: 'title',
              key: 'title',
            },
            {
              title: '短链码',
              dataIndex: 'short_code',
              key: 'short_code',
              render: (code: string) => code ? <Text code copyable>{code}</Text> : '-',
            },
            {
              title: '总访问量',
              dataIndex: 'total_visits',
              key: 'total_visits',
              sorter: (a, b) => a.total_visits - b.total_visits,
            },
            {
              title: '默认跳转',
              dataIndex: 'default_redirects',
              key: 'default_redirects',
              render: (value, record) => (
                <div>
                  <Text type="success">{value}</Text>
                  <Progress
                    percent={Math.round((value / record.total_visits) * 100)}
                    size="small"
                    showInfo={false}
                  />
                </div>
              ),
            },
            {
              title: '拦截跳转',
              dataIndex: 'intercept_redirects',
              key: 'intercept_redirects',
              render: (value, record) => (
                <div>
                  <Text type="danger">{value}</Text>
                  <Progress
                    percent={Math.round((value / record.total_visits) * 100)}
                    size="small"
                    showInfo={false}
                    status="exception"
                  />
                </div>
              ),
            },
            {
              title: '成功率',
              dataIndex: 'success_rate',
              key: 'success_rate',
              render: (value) => (
                <Tag color={value > 80 ? 'green' : value > 60 ? 'orange' : 'red'}>
                  {value}%
                </Tag>
              ),
              sorter: (a, b) => a.success_rate - b.success_rate,
            },
          ]}
          rowKey="id"
          pagination={false}
        />
      </Card>

      {/* 拦截原因统计表格 */}
      <div style={{ marginTop: 24 }}>
        <InterceptReasonsTable
          shortCode={appliedShortCode}
          startDate={dateRange[0].format('YYYY-MM-DD')}
          endDate={dateRange[1].format('YYYY-MM-DD')}
        />
      </div>
    </div>
  );
};

export default Statistics; 